<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import countries from 'shared/constants/countries';
import FormField from './FormField.vue';
import DateTimeField from './DateTimeField.vue';

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  errors: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const KEY = 'CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.CAR';

const pickup = () => props.modelValue.pickup || {};
const dropoff = () => props.modelValue.dropoff || {};
const vehicle = () => props.modelValue.vehicle || {};
const driver = () => props.modelValue.driver || {};

const set = patch =>
  emit('update:modelValue', { ...props.modelValue, ...patch });

const setNested = (key, patch) =>
  set({ [key]: { ...(props.modelValue[key] || {}), ...patch } });

// Default to "return to the same place" unless the saved booking already differs.
// Both fields are compared: automated Travel Router rows carry only a
// `locationCode`, so a name-only check would hide a real one-way drop-off.
// Seeded once from the initial modelValue; the parent must `:key` this component
// by booking id so switching bookings remounts it.
const sameAsPickup = ref(
  (!dropoff().locationName && !dropoff().locationCode) ||
    (dropoff().locationName === pickup().locationName &&
      dropoff().locationCode === pickup().locationCode)
);

const setPickup = patch => {
  const next = { ...pickup(), ...patch };
  const update = { pickup: next };
  if (sameAsPickup.value) {
    update.dropoff = {
      ...dropoff(),
      locationName: next.locationName,
      locationCode: next.locationCode,
    };
  }
  set(update);
};

watch(sameAsPickup, same => {
  if (same) {
    setNested('dropoff', {
      locationName: pickup().locationName,
      locationCode: pickup().locationCode,
    });
  }
});

const transmissionOptions = [
  { value: 'automatic', label: t(`${KEY}.TRANSMISSION_OPTIONS.AUTOMATIC`) },
  { value: 'manual', label: t(`${KEY}.TRANSMISSION_OPTIONS.MANUAL`) },
];

const countryOptions = countries.map(country => ({
  value: country.id,
  label: `${country.emoji} ${country.name}`,
}));
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Pickup -->
    <div class="flex flex-col gap-2">
      <p class="text-xs font-semibold uppercase tracking-wide text-n-slate-10">
        {{ t(`${KEY}.PICKUP`) }}
      </p>
      <div class="grid grid-cols-3 gap-2">
        <Input
          class="col-span-2"
          size="sm"
          :label="t(`${KEY}.LOCATION`)"
          :placeholder="t(`${KEY}.LOCATION_PLACEHOLDER`)"
          :model-value="pickup().locationName"
          :message="errors['pickup.locationName']"
          :message-type="errors['pickup.locationName'] ? 'error' : 'info'"
          @update:model-value="setPickup({ locationName: $event })"
        />
        <Input
          size="sm"
          :label="t(`${KEY}.LOCATION_CODE`)"
          :placeholder="t(`${KEY}.LOCATION_CODE_PLACEHOLDER`)"
          :model-value="pickup().locationCode"
          @update:model-value="setPickup({ locationCode: $event })"
        />
      </div>
      <DateTimeField
        :model-value="pickup().at"
        :error="errors['pickup.at']"
        @update:model-value="setNested('pickup', { at: $event })"
      />
    </div>

    <!-- Dropoff -->
    <div class="flex flex-col gap-2">
      <p class="text-xs font-semibold uppercase tracking-wide text-n-slate-10">
        {{ t(`${KEY}.DROPOFF`) }}
      </p>
      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <Checkbox v-model="sameAsPickup" />
        {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.SAME_AS_PICKUP') }}
      </label>
      <div v-if="!sameAsPickup" class="grid grid-cols-3 gap-2">
        <Input
          class="col-span-2"
          size="sm"
          :label="t(`${KEY}.LOCATION`)"
          :placeholder="t(`${KEY}.LOCATION_PLACEHOLDER`)"
          :model-value="dropoff().locationName"
          @update:model-value="setNested('dropoff', { locationName: $event })"
        />
        <Input
          size="sm"
          :label="t(`${KEY}.LOCATION_CODE`)"
          :placeholder="t(`${KEY}.LOCATION_CODE_PLACEHOLDER`)"
          :model-value="dropoff().locationCode"
          @update:model-value="setNested('dropoff', { locationCode: $event })"
        />
      </div>
      <DateTimeField
        :model-value="dropoff().at"
        :error="errors['dropoff.at']"
        @update:model-value="setNested('dropoff', { at: $event })"
      />
    </div>

    <!-- Vehicle -->
    <div class="flex flex-col gap-2">
      <p class="text-xs font-semibold uppercase tracking-wide text-n-slate-10">
        {{ t(`${KEY}.VEHICLE`) }}
      </p>
      <div class="grid grid-cols-2 gap-2">
        <Input
          size="sm"
          :label="t(`${KEY}.CATEGORY`)"
          :placeholder="t(`${KEY}.CATEGORY_PLACEHOLDER`)"
          :model-value="vehicle().category"
          @update:model-value="setNested('vehicle', { category: $event })"
        />
        <Input
          size="sm"
          :label="t(`${KEY}.MODEL`)"
          :placeholder="t(`${KEY}.MODEL_PLACEHOLDER`)"
          :model-value="vehicle().sampleModel"
          @update:model-value="setNested('vehicle', { sampleModel: $event })"
        />
        <FormField :label="t(`${KEY}.TRANSMISSION`)">
          <Select
            :model-value="vehicle().transmission"
            :options="transmissionOptions"
            :placeholder="
              t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.SELECT_PLACEHOLDER')
            "
            class="!w-full"
            @update:model-value="setNested('vehicle', { transmission: $event })"
          />
        </FormField>
        <Input
          size="sm"
          type="number"
          min="1"
          :label="t(`${KEY}.SEATS`)"
          :model-value="vehicle().seats"
          @update:model-value="setNested('vehicle', { seats: $event })"
        />
        <Input
          size="sm"
          type="number"
          min="1"
          :label="t(`${KEY}.DOORS`)"
          :model-value="vehicle().doors"
          @update:model-value="setNested('vehicle', { doors: $event })"
        />
        <Input
          size="sm"
          :label="t(`${KEY}.FUEL`)"
          :placeholder="t(`${KEY}.FUEL_PLACEHOLDER`)"
          :model-value="vehicle().fuel"
          @update:model-value="setNested('vehicle', { fuel: $event })"
        />
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <Checkbox
            :model-value="Boolean(vehicle().airCon)"
            @update:model-value="setNested('vehicle', { airCon: $event })"
          />
          {{ t(`${KEY}.AIR_CON`) }}
        </label>
        <Input
          size="sm"
          :label="t(`${KEY}.PROVIDER`)"
          :placeholder="t(`${KEY}.PROVIDER_PLACEHOLDER`)"
          :model-value="modelValue.provider"
          @update:model-value="set({ provider: $event })"
        />
      </div>
    </div>

    <!-- Driver -->
    <div class="flex flex-col gap-2">
      <p class="text-xs font-semibold uppercase tracking-wide text-n-slate-10">
        {{ t(`${KEY}.DRIVER`) }}
      </p>
      <div class="grid grid-cols-2 gap-2">
        <Input
          size="sm"
          :label="t(`${KEY}.FIRST_NAME`)"
          :model-value="driver().firstName"
          @update:model-value="setNested('driver', { firstName: $event })"
        />
        <Input
          size="sm"
          :label="t(`${KEY}.LAST_NAME`)"
          :model-value="driver().lastName"
          @update:model-value="setNested('driver', { lastName: $event })"
        />
        <Input
          size="sm"
          type="number"
          min="18"
          :label="t(`${KEY}.AGE`)"
          :model-value="driver().age"
          @update:model-value="setNested('driver', { age: $event })"
        />
        <FormField :label="t(`${KEY}.COUNTRY`)">
          <ComboBox
            :model-value="driver().countryOfResidence"
            :options="countryOptions"
            :placeholder="t(`${KEY}.COUNTRY_PLACEHOLDER`)"
            :search-placeholder="t(`${KEY}.COUNTRY_SEARCH`)"
            :empty-state="t(`${KEY}.COUNTRY_EMPTY`)"
            @update:model-value="
              setNested('driver', { countryOfResidence: $event })
            "
          />
        </FormField>
        <Input
          size="sm"
          type="email"
          :label="t(`${KEY}.EMAIL`)"
          :model-value="driver().email"
          @update:model-value="setNested('driver', { email: $event })"
        />
        <Input
          size="sm"
          :label="t(`${KEY}.FLIGHT_NUMBER`)"
          :placeholder="t(`${KEY}.FLIGHT_NUMBER_PLACEHOLDER`)"
          :model-value="modelValue.flightNumber"
          @update:model-value="set({ flightNumber: $event })"
        />
      </div>
    </div>

    <TextArea
      :model-value="modelValue.notes"
      :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.NOTES')"
      :placeholder="
        t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.NOTES_PLACEHOLDER')
      "
      :max-length="2000"
      @update:model-value="set({ notes: $event })"
    />
  </div>
</template>
